/**
 * ADMINISTRATION VOCABULARY → HEBREW.
 *
 * The same discipline as `denialText.ts`, applied to the administration surfaces: every code the
 * server can send has one Hebrew phrase, stated once, in a pure module a test can enumerate. There
 * is no `?? code` fallback that would put `required_for_admins` on a screen (09 §9.1 V3 — the ONLY
 * technical string a customer ever sees is a support reference code).
 *
 * `contract.mirror.test.ts` fails the build when `packages/contracts` or `packages/authn` grows a
 * member this file has no Hebrew for, in exactly the way it already does for `DENY_REASONS`. A
 * status with no Hebrew is an English enum on a Hebrew screen with extra steps.
 *
 * This module DECIDES NOTHING. It maps codes to words. It grants nothing, it hides nothing, and no
 * screen may branch on the tone it returns for anything but chrome (09 §9.5 A3: colour is never the
 * only carrier of meaning, so every tone is accompanied by the word itself).
 */

import { organisationWording } from './organisationWording';

// The closed set itself, so `accessLevelText` can ask whether a plain string is one of the three
// rather than casting and hoping.
import { ACCESS_LEVELS } from './contract';

import type {
  AccessLevel,
  AuditActorKind,
  AuditOutcome,
  CapacityKind,
  CompanyLifecycleStatus,
  ConnectorStatus,
  MembershipStatus,
  PlatformRole,
  SeededTenantRole,
  SubscriptionState,
  TenantMfaPolicy,
  TenantStatus,
} from './contract';

/** Chrome only. A3: every use of a tone is accompanied by the Hebrew word it decorates. */
export type Tone = 'ok' | 'warn' | 'error' | 'muted';

export interface TermHe {
  readonly labelHe: string;
  readonly tone: Tone;
}

const TENANT_STATUS_HE: Readonly<Record<TenantStatus, TermHe>> = {
  active: { labelHe: 'פעיל', tone: 'ok' },
  trial: { labelHe: 'תקופת ניסיון', tone: 'warn' },
  suspended: { labelHe: 'מושהה', tone: 'error' },
  closed: { labelHe: 'סגור', tone: 'muted' },
};

export function tenantStatusHe(status: TenantStatus): TermHe {
  return TENANT_STATUS_HE[status];
}

/**
 * 08 §5.1 + §6.0. `draft` is not a state that grants anything, and the Hebrew says so rather than
 * implying a subscription that merely has not started yet.
 */
const SUBSCRIPTION_STATE_HE: Readonly<Record<SubscriptionState, TermHe>> = {
  draft: { labelHe: 'טיוטה — טרם הופעל', tone: 'muted' },
  trialing: { labelHe: 'תקופת ניסיון', tone: 'warn' },
  active: { labelHe: 'פעיל', tone: 'ok' },
  past_due: { labelHe: 'קיים חוב פתוח', tone: 'warn' },
  suspended: { labelHe: 'מושהה', tone: 'error' },
  cancelled: { labelHe: 'בוטל — פעיל עד תום התקופה', tone: 'warn' },
  expired: { labelHe: 'הסתיים', tone: 'error' },
};

export function subscriptionStateHe(state: SubscriptionState): TermHe {
  return SUBSCRIPTION_STATE_HE[state];
}

/**
 * NOT a subscription state. The absence of one, said in words.
 *
 * It is deliberately NOT added to `SUBSCRIPTION_STATES`: that set is what the server can STORE,
 * and "there is no row" is not one of its members. Adding it there would put a display category
 * into the contract's vocabulary, which is how a second namespace starts.
 */
const NO_SUBSCRIPTION_HE: TermHe = { labelHe: 'אין מנוי', tone: 'muted' };

/**
 * The term for a customer that MAY have no subscription at all — which every customer is between
 * step 1 and step 2 of provisioning.
 *
 * ── THIS EXISTS BECAUSE THE PRODUCT CRASHED FOR WANT OF IT ─────────────────────────────
 *
 * `subscriptionStateHe` is TOTAL over the closed set and indexes straight into the map, so a value
 * outside the set returns `undefined` and the caller's `term.tone` throws. Four screens did exactly
 * that on Staging on 2026-10-18 — `/platform`, the customer list, the customer card and billing —
 * and a thrown render is a BLANK PAGE with no reference code.
 *
 * `undefined` is accepted beside `null` ON PURPOSE: a browser holding an older build of this app
 * talks to a server that does not send the field yet, and the honest answer there is the same
 * sentence, not a crash. It is not an invitation to leave the field out.
 */
export function subscriptionStateOrNoneHe(state: SubscriptionState | null | undefined): TermHe {
  return state === null || state === undefined ? NO_SUBSCRIPTION_HE : SUBSCRIPTION_STATE_HE[state];
}

const MEMBERSHIP_STATUS_HE: Readonly<Record<MembershipStatus, TermHe>> = {
  invited: { labelHe: 'הוזמן — טרם הצטרף', tone: 'warn' },
  active: { labelHe: 'פעיל', tone: 'ok' },
  suspended: { labelHe: 'מושהה', tone: 'warn' },
  disabled: { labelHe: 'מושבת', tone: 'muted' },
};

export function membershipStatusHe(status: MembershipStatus): TermHe {
  return MEMBERSHIP_STATUS_HE[status];
}

/**
 * The eight company lifecycle states, verbatim from `packages/contracts/src/enums.ts`.
 *
 * 09 decision 10: a company's state is DISPLAYED, never inferred — `company_tags.json` held no
 * `active` key at all for 167 of 313 companies, and two code paths resolved that absence in opposite
 * directions. `<LifecycleChip>` renders these as chips and `lifecycleText.ts` puts them in sentences,
 * from this one map.
 */
const LIFECYCLE_STATUS_HE: Readonly<Record<CompanyLifecycleStatus, string>> = {
  prospect: 'לקוח פוטנציאלי',
  active: 'פעילה',
  dormant: 'רדומה',
  former_client: 'לקוח לשעבר',
  merged_into: 'מוזגה',
  quarantined: 'בהסגר',
  source_unavailable: 'מקור הנתונים אינו זמין',
  review_required: 'ממתינה לבדיקה',
};

export function lifecycleStatusHe(status: CompanyLifecycleStatus): string {
  return LIFECYCLE_STATUS_HE[status];
}

/**
 * 01 §A5. Note the absence: there is no `deny` level, so there is no Hebrew for one.
 *
 * ── TWO WORDS FOR THREE VALUES, AND THAT IS NOT A LOSS. **2026-10-28.** ────────────────────────
 *
 * Owner: *"אני לא מבין מה זה צפייה, עדכון, אישור."* The reason he could not is that two of them do
 * the same thing: `ACCESS_LEVEL_PERMITS` gives `write` and `approve` the SAME four answers, so the
 * gate cannot tell them apart and neither could he.
 *
 * A stored `approve` is therefore called what it DOES — עריכה — and the product no longer offers it
 * as a separate choice (`OFFERED_ACCESS_LEVELS`). The value is untouched in the database, in the
 * vocabulary and in the gate; only the word is honest now.
 *
 * `עדכון` became `עריכה` in the same pass: a level is what somebody may DO with a company's data,
 * and *edit* says that where *update* reads like a system operation.
 */
const ACCESS_LEVEL_HE: Readonly<Record<AccessLevel, string>> = {
  read: 'צפייה בלבד',
  write: 'צפייה ועריכה',
  approve: 'צפייה ועריכה',
};

export function accessLevelHe(level: AccessLevel): string {
  return ACCESS_LEVEL_HE[level];
}

/**
 * THE SAME THREE WORDS, FOR A LEVEL THAT ARRIVED AS A PLAIN STRING. **2026-10-28.**
 *
 * `accessLevelHe` takes the closed union, which is right for every caller that holds a typed grant.
 * Two reads added in this round carry the level out of a COLUMN — the invitation list and the grant
 * outcomes — so their wire type is `string`, and a cast at each call site would be two places
 * asserting something neither one checked.
 *
 * A value outside the three is RETURNED AS IT IS rather than mapped to a plausible one. An invented
 * label on an unknown level is 07 N-5's shape exactly: a missing value became a working default on
 * 5 of 313 companies, and nobody saw it because the default looked like an answer.
 */
export function accessLevelText(level: string): string {
  return (ACCESS_LEVELS as readonly string[]).includes(level) ? ACCESS_LEVEL_HE[level as AccessLevel] : level;
}

/**
 * 01 §A4 — the five tenant role codes a tenant-provisioning path creates per office. They are not
 * seeded by a migration and cannot be; the names below are what those codes will be CALLED.
 */
/*
 * ── THE FOUR NAMES THE OWNER ASKED FOR. **2026-10-28.** ────────────────────────────────────────
 *
 *   בעלים — ארגון → **בעלים** · מנהל ארגון → **מנהל** · עובד ארגון → **עובד ראשי** ·
 *   צופה — לקוח → **צפייה בלבד**
 *
 * *"השינויים האלה הם בשמות המוצגים בלבד, בלי לשנות הרשאות בפועל."* — and that is what this
 * is. **No capability moved, no migration was added, `0010` was not rewritten, and the role CODE,
 * which is the only thing the gate reads, is untouched.**
 *
 * `tenant_billing_contact` IS DELIBERATELY UNCHANGED: the Owner asked what it grants before
 * deciding anything about it, and *"אל תמחק או תשנה את ההרשאות שלו לפני שנחליט"*. A rename is
 * not a permission change, but it is still a change to something under discussion.
 *
 * ── AND IT IS ONE MAP, NOT TWO ────────────────────────────────────────────────────────────────
 *
 * The first draft of this change declared a SECOND map beside this one, keyed the same way. Two
 * tables of role names is how a role comes to be called one thing in the user card and another in
 * the composer. `roleLabel` reads THIS one.
 */
const TENANT_ROLE_HE: Readonly<Record<SeededTenantRole, string>> = {
  tenant_owner: 'בעלים',
  tenant_admin: 'מנהל',
  tenant_member: 'עובד ראשי',
  tenant_billing_contact: 'איש קשר לחיובים',
  client_viewer: 'צפייה בלבד',
};

export function tenantRoleHe(role: SeededTenantRole): string {
  return TENANT_ROLE_HE[role];
}

/** 04 §6.2 — the four platform roles. A disjoint namespace from the tenant roles above. */
const PLATFORM_ROLE_HE: Readonly<Record<PlatformRole, string>> = {
  platform_owner: 'בעלים של הפלטפורמה',
  platform_operator: 'מפעיל הפלטפורמה',
  platform_support: 'תמיכה',
  platform_auditor: 'מבקר',
};

export function platformRoleHe(role: PlatformRole): string {
  return PLATFORM_ROLE_HE[role];
}

const CAPACITY_KIND_HE: Readonly<Record<CapacityKind, { readonly nounHe: string; readonly unitHe: string }>> = {
  company: { nounHe: 'חברות', unitHe: 'חברות' },
  seat: { nounHe: 'משתמשים', unitHe: 'מושבים' },
  module_company: { nounHe: 'חברות במודול', unitHe: 'חברות' },
};

export function capacityKindHe(kind: CapacityKind): { readonly nounHe: string; readonly unitHe: string } {
  return CAPACITY_KIND_HE[kind];
}

/** 07 §2.3 — `status` is an enum, never an `is_deleted` flag, and only ACTIVE may serve data. */
const CONNECTOR_STATUS_HE: Readonly<Record<ConnectorStatus, TermHe>> = {
  PROVISIONING: { labelHe: 'בהקמה', tone: 'warn' },
  ACTIVE: { labelHe: 'פעיל', tone: 'ok' },
  SUSPENDED: { labelHe: 'מושהה', tone: 'warn' },
  FAILED: { labelHe: 'תקול', tone: 'error' },
  REVOKED: { labelHe: 'בוטל', tone: 'muted' },
};

export function connectorStatusHe(status: ConnectorStatus): TermHe {
  return CONNECTOR_STATUS_HE[status];
}

const AUDIT_OUTCOME_HE: Readonly<Record<AuditOutcome, TermHe>> = {
  allow: { labelHe: 'בוצע', tone: 'ok' },
  deny: { labelHe: 'נדחה', tone: 'error' },
};

export function auditOutcomeHe(outcome: AuditOutcome): TermHe {
  return AUDIT_OUTCOME_HE[outcome];
}

/**
 * ACTOR KIND → HEBREW. **X10 — the fifth divergence, and the second blank column.**
 *
 * This map used to be keyed on a five-member vocabulary that the database does not permit and no
 * producer writes. `CK_audit_actor_kind` (`db/migrations/0004__audit.up.sql:72-73`) admits SEVEN
 * values and `AUDIT_ACTOR_KINDS` (`packages/contracts/src/enums.ts`) declares those seven verbatim;
 * the web mirror had dropped `platform`, `connector` and `system` and invented `platform_support`,
 * which is a PLATFORM ROLE code, not an actor kind.
 *
 * `platform` is the member that mattered. `domain-audit.ts:103` files a PLATFORM-session act under
 * it, and inside a tenant's bound context that is precisely a support crossing into the office's own
 * data — the single row this screen exists to show. The lookup was an UNDEFAULTED record access, so
 * such a row's `מי` cell evaluated to `undefined` and rendered NOTHING. Two columns blank on one
 * table, for two unrelated reasons.
 *
 * ── ALL SEVEN NOW CARRY A NAME — X7 ─────────────────────────────────────────────────────────────
 *
 * X10 shipped with `connector` and `system` falling back to `לא מזוהה`, which is the string
 * `anonymous` carries, so a machine act and an unidentified caller rendered as the same cell. X7
 * supplies the two missing names (PRODUCT-EXPECTATION_GLPOINT-S4W2-X7-FINAL-COPY.md §4.1) and three
 * different facts now read as three different cells:
 *
 *   `anonymous` — a caller who was never identified.
 *   `service`   — an identified machine principal.
 *   `system`    — NO authenticated principal at all; the authority is deployment access to the host
 *                 (`packages/db/src/platform-staff-mint.ts:139-152`, which records that `'platform'`
 *                 there *"would claim a platform session that does not exist"*).
 *   `connector` — a data connector installed at a customer's office; the gate derives the kind from
 *                 the bound principal (`packages/authz/src/gate.ts:173-188`) and its rows carry the
 *                 tenant, so this IS reachable on an office's own audit screen. `מחבר` is the word
 *                 the product already uses for the object (`PlatformHealthScreen.tsx:55-56`), so the
 *                 label joins an existing vocabulary instead of opening a second one. It names a
 *                 MACHINE, which is the point of the column: it must not look like a person.
 *
 * ── AND THE `platform` STRING IS CORRECTED, NOT THE KEY — X7 §4.2 ──────────────────────────────
 *
 * X10 gave `platform` the string `תמיכת הפלטפורמה`, which asserts the SUPPORT PROGRAMME. The code
 * asserts a SESSION CONTEXT: `actorKindOf` returns `platform` for ANY PLATFORM-session user fact —
 * `return ctx.sessionContext === 'PLATFORM' ? 'platform' : 'user'`
 * (`apps/api/src/domain/domain-audit.ts:101-104`) — with no reference to a grant. It is true today
 * only because a platform principal cannot bind a tenant context without one, which is a property of
 * the GATE and not of this label; the day any non-support platform act is filed against a tenant,
 * the office's trail would say "support" about something that was not support. A label must not carry
 * a claim its producer does not make, and the support fact already has its own carrier — the
 * `supportGrantUid` column, which *"is never hidden"* (`contract.ts:755-756`). So the KEY stands and
 * the STRING becomes `צוות הפלטפורמה`.
 */
const AUDIT_ACTOR_KIND_HE: Readonly<Partial<Record<AuditActorKind, string>>> = {
  // In `AUDIT_ACTOR_KINDS` order (`packages/contracts/src/enums.ts:274-282`), so the mirror can pin
  // this map's key order against the server's own vocabulary rather than against a list kept by hand.
  user: 'משתמש',
  platform: 'צוות הפלטפורמה',
  service: 'שירות',
  job: 'תהליך מתוזמן',
  connector: 'מחבר נתונים שהותקן בארגון',
  system: 'המערכת (ללא משתמש מזוהה)',
  anonymous: 'לא מזוהה',
};

/**
 * DEFAULTED, and that is the fix. An actor kind with no name must render a word, never an empty cell.
 *
 * All seven CURRENT members are named above, so the fallback is now reachable only by a kind this
 * build has never seen — a future `CK_audit_actor_kind` member, before anyone names it. It is kept
 * for exactly that, and the mirror asserts it still returns Hebrew.
 *
 * Takes `string` rather than `AuditActorKind`: the value arrives from the wire, and the narrower
 * parameter was a type asserting something about a runtime value nobody had checked.
 */
export function auditActorKindHe(kind: string): string {
  return AUDIT_ACTOR_KIND_HE[kind as AuditActorKind] ?? 'לא מזוהה';
}

/** For the mirror test: the kinds that actually carry a name of their own. */
export function namedAuditActorKinds(): readonly string[] {
  return Object.keys(AUDIT_ACTOR_KIND_HE);
}

const MFA_POLICY_HE: Readonly<Record<TenantMfaPolicy, string>> = {
  optional: 'אימות דו-שלבי אינו חובה בארגון',
  required_for_admins: 'אימות דו-שלבי חובה למנהלי הארגון',
  required_for_all: 'אימות דו-שלבי חובה לכל משתמשי הארגון',
};

export function mfaPolicyHe(policy: TenantMfaPolicy): string {
  return MFA_POLICY_HE[policy];
}

/**
 * CAPABILITY → HEBREW, verbatim from `descriptionHe` in `packages/contracts/src/capabilities.ts`.
 *
 * Verbatim is the point: 04 §7.1 / 02 §5.1 make the CODE the declaration of what exists, and a
 * second Hebrew name invented here would be a second catalogue. The mirror test reads that file and
 * fails if a phrase drifts by one character.
 *
 * Only the TENANT set is listed. The tenant set is open by design — a module contributes its own
 * when it migrates — so `capabilityDescriptionHe` answers `null` for a code it has never seen, and
 * the roles screen writes that absence out rather than printing the code.
 */
const CAPABILITY_HE: Readonly<Record<string, string>> = {
  'companies.company.read': 'צפייה בחברות',
  'companies.company.manage': 'ניהול חברות',
  'companies.identifier.read': 'צפייה במזהים חיצוניים',
  'companies.identifier.manage': 'ניהול מזהים חיצוניים',
  'companies.conflict.read': 'צפייה בסתירות זהות',
  'companies.conflict.resolve': 'הכרעה בסתירת זהות',
  'companies.group.manage': 'ניהול קבוצות חברות',
  'companies.portfolio.manage': 'ניהול תיקי עבודה',
  'companies.company.create': 'הקמת חברה',
  'companies.assignment.manage': 'ניהול הרשאות לחברות',
  'tenant.user.manage': 'ניהול משתמשים',
  'tenant.role.manage': 'ניהול תפקידים',
  'tenant.settings.read': 'צפייה בהגדרות המשרד',
  'tenant.branding.manage': 'ניהול מיתוג המשרד',
  'tenant.user.invite': 'הזמנת משתמש',
  'tenant.settings.manage': 'ניהול הגדרות המשרד',
  'audit.read': 'צפייה ביומן ביקורת',
  'security.support_grant.read': 'צפייה בהרשאות תמיכה',
  'security.support_grant.revoke': 'ביטול הרשאת תמיכה',
  'billing.plan.read': 'צפייה בתכנית',
  'billing.usage.read': 'צפייה בשימוש',
  'billing.invoice.read': 'צפייה בחשבוניות',
  'billing.invoice.download': 'הורדת חשבונית',
  'billing.payment_method.update': 'עדכון אמצעי תשלום',
  'billing.profile.update': 'עדכון פרטי חיוב',
  'billing.change.request': 'בקשת שינוי מסחרי',
  'billing.subscription.cancel': 'ביטול מנוי',
  /* 0044. Without this line the role editor cannot offer it — the 0027 defect, by name. */
  'billing.module.purchase': 'רכישת מודול',
  /*
   * 0046. VERBATIM from `capabilities.ts`, which is what this map is for: the role composer
   * quotes the declaration rather than paraphrasing it, so a role an administrator builds
   * carries the authority the migration seeded under the words the migration used.
   */
  /* ITEM 4, 0051. */
  'billing.plan.change': 'שינוי תכנית',
  'billing.export.request': 'בקשת ייצוא נתונים',
  /*
   * ── THE FOUR FROM 0027, AND THE SCREEN COULD NOT GRANT THEM. **Found 2026-11-01.** ──────────
   *
   * Migration 0027 created `tasks.item.read`, `tasks.item.act`, `tasks.item.manage` and
   * `tasks.rule.manage`, declared them in `packages/contracts/src/capabilities.ts` with these
   * exact Hebrew labels, seeded them into `core.capability`, and granted them to the three role
   * TEMPLATES. And `TenantRoleAuthoringScreen` offers exactly `describedCapabilityCodes()` —
   * *"a code with no Hebrew is never offered as a control"* — so the only screen in the product
   * that can grant a capability DID NOT OFFER THESE FOUR.
   *
   * An office administrator therefore could not give a colleague `tasks.item.read`, and that
   * colleague's two task cards on the home screen would be refused with no way for anybody in
   * the office to fix it. The capability existed, was correct, and was ungrantable: the same
   * shape of absence as a control that no mode dispatches.
   *
   * It stayed invisible because `contract.mirror.test.ts` asserted ONE DIRECTION — that this map
   * names no capability the catalogue does not declare. The other direction is asserted now.
   *
   * The Hebrew is the catalogue's, character for character, which that same file checks.
   */
  'tasks.item.read': 'צפייה במשימות',
  'tasks.item.act': 'טיפול במשימה שלי',
  'tasks.item.manage': 'ניהול משימות',
  'tasks.rule.manage': 'תחזוקת מועדי דיווח',
};

/** `null` ⇒ this build has no Hebrew for that capability. The screen says so; it never prints it. */
export function capabilityDescriptionHe(code: string): string | null {
  /*
   * THE MAP QUOTES THE DECLARATION; THIS LINE IS WHERE THE PRODUCT'S WORDING IS APPLIED.
   *
   * `contract.mirror.test.ts` requires every entry above to be the capability catalogue's Hebrew
   * VERBATIM -- *"a second name here would be a second catalogue"* -- and that catalogue generates
   * `0005__reference_data.up.sql`, which has already run on the Owner's server. Three of its labels
   * still carry the noun he retired, and he ruled out changing the schema to carry a rename.
   *
   * So the map stays a quotation and the ACCESSOR, which is the only way a label reaches a screen,
   * returns the current wording. Neither the declaration nor the database is touched, and no screen
   * shows the retired word.
   */
  const declared = CAPABILITY_HE[code];
  return declared === undefined ? null : organisationWording(declared);
}

/**
 * A ROLE NAME AS A SCREEN MAY SHOW IT. **Owner section 7, 2026-10-27.**
 *
 * Reported: *"עדיין מופיע בתפקיד ״בעלים — משרד״. יש להשלים את הסרת ״משרד״ ממלל הממשק, כולל תוויות
 * שמקורן בנתונים קיימים, בלי לשכתב מיגרציות ישנות."*
 *
 * The source is `db/migrations/0010__tenant_role_bootstrap_seed.up.sql`, which has already run on
 * the Owner's server. THE SAME ANSWER THE CAPABILITY LABELS ABOVE ALREADY USE, and for the reason
 * written there: the declaration and the database are left alone, and the ACCESSOR — the only way
 * the label reaches a screen — returns the current wording. One rule, one implementation, and the
 * control below keeps it that way.
 *
 * ── AND IT STOPS AT NAMES THE CUSTOMER WROTE, WHICH IS THE WHOLE REASON IT TAKES THE ROLE ──────
 *
 * `organisationWording` is total and mechanical: handed any string, it rewrites it. Applied to a
 * role a tenant created and named themselves, it would silently edit a customer's own words —
 * including the legitimate case of an organisation that really is an office and named a role so.
 * `isPersonal` is the server's own fact about which of the two a role is, so a seeded role is
 * corrected and a hand-made one is shown exactly as it was typed.
 */
/**
 * A ROLE NAME AS A SCREEN MAY SHOW IT. **Owner section 7, 2026-10-27; renamed 2026-10-28.**
 *
 * Reported then: *"עדיין מופיע בתפקיד בעלים — משרד. יש להשלים את הסרת משרד ממלל הממשק,
 * כולל תוויות שמקורן בנתונים קיימים, בלי לשכתב מיגרציות ישנות."*
 *
 * The source is `db/migrations/0010__tenant_role_bootstrap_seed.up.sql`, which has already run on
 * the Owner's server. The declaration and the database are left alone, and the ACCESSOR — the only
 * way a label reaches a screen — returns the current wording.
 *
 * ── THREE ANSWERS, IN ONE ORDER, AND THE ORDER IS THE DESIGN ────────────────────────────────
 *
 * 1. **A role the customer made is their own words**, returned untouched. Checked FIRST, so a
 *    hand-made role that happened to carry a seeded code could never be renamed out from under
 *    them. `isPersonal` is the server's own fact about which of the two a role is.
 * 2. **A seeded role is called by its asked-for name**, taken from `TENANT_ROLE_HE` by CODE. By
 *    code and not by text: matching `מנהל ארגון` as a string would rename a customer's role
 *    that happens to read that way, and would stop working the day the seed wording changes.
 * 3. **Anything else keeps the retired-word correction**, which is total and mechanical.
 */
export function roleLabel(role: {
  readonly displayNameHe: string;
  readonly isPersonal: boolean;
  readonly roleCode?: string;
}): string {
  if (role.isPersonal) return role.displayNameHe;

  const code = role.roleCode;
  if (code !== undefined && code in TENANT_ROLE_HE) {
    return TENANT_ROLE_HE[code as SeededTenantRole];
  }

  return organisationWording(role.displayNameHe);
}


/**
 * ══ PERMISSIONS, BY AREA. **Owner, 2026-10-28.** ═══════════════════════════════════════════════
 *
 * *"צילום אייקונט ששלחתי הוא הבסיס לממשק ההרשאות המבוקש: רשימה מסודרת לפי תחומים ומודולים, עם
 * אפשרות לפתוח כל תחום ולבחור פעולות. שינוי שמות התפקידים לבדו אינו משלים את הבקשה הזאת."*
 *
 * The role composer rendered **28 checkboxes in one flat run**. Nobody can hold twenty-eight
 * unrelated permissions in their head at once, and the reader had no way to ask the only question
 * that matters while composing a role — *what does this role do about companies?*
 *
 * ── THE GROUPING WAS ALREADY IN THE DATA, AND THE SCREEN THREW IT AWAY ──────────────────────
 *
 * Every capability code is `<area>.<thing>.<action>`. The first segment IS the area, and it is the
 * server's own classification rather than one invented here: it is the same prefix the gate matches
 * on and the same one `0005` seeds. So this does not add a taxonomy — it stops discarding one.
 *
 * An area with no Hebrew name would be invisible, which is how a capability silently stops being
 * offered. `capabilityAreas` therefore returns EVERY area it finds, named or not, and the control
 * in `adminScreens.test.ts` asserts that every described code lands in a named area. A new prefix
 * fails that case instead of disappearing from the composer.
 */
const CAPABILITY_AREA_HE: Readonly<Record<string, string>> = {
  companies: 'חברות',
  tenant: 'הארגון והמשתמשים',
  billing: 'מנוי וחיוב',
  security: 'אבטחה',
  audit: 'יומן ביקורת',
  /*
   * ADDED WITH THE FOUR ABOVE. Without it the fallback below would have headed a section of a
   * Hebrew screen with the Latin word `tasks` — which is the same defect the Owner named about
   * `policy_rate` on the home screen, in a different place.
   */
  tasks: 'משימות ומועדי דיווח',
};

/** The order the areas are shown in — most-used first, and billing last because it is narrowest. */
const AREA_ORDER: readonly string[] = ['companies', 'tasks', 'tenant', 'security', 'audit', 'billing'];

export interface CapabilityArea {
  readonly key: string;
  /** The area's Hebrew name, or the raw prefix when the area is one nothing has named yet. */
  readonly titleHe: string;
  readonly codes: readonly string[];
}

/**
 * The given codes, grouped by their area and ordered.
 *
 * Codes keep the catalogue's own order inside an area, so a reader who knew the flat list still
 * finds `צפייה` before `ניהול`. An unknown area sorts to the end rather than being dropped.
 */
export function capabilityAreas(codes: readonly string[]): readonly CapabilityArea[] {
  const byArea = new Map<string, string[]>();
  for (const code of codes) {
    const key = code.split('.')[0] ?? code;
    const existing = byArea.get(key);
    if (existing === undefined) byArea.set(key, [code]);
    else existing.push(code);
  }

  return [...byArea.entries()]
    .sort(([a], [b]) => {
      const ia = AREA_ORDER.indexOf(a);
      const ib = AREA_ORDER.indexOf(b);
      // An area nobody ordered goes last, and two of them keep the order they were found in.
      return (ia === -1 ? AREA_ORDER.length : ia) - (ib === -1 ? AREA_ORDER.length : ib);
    })
    .map(([key, areaCodes]) => ({
      key,
      titleHe: CAPABILITY_AREA_HE[key] ?? key,
      codes: areaCodes,
    }));
}

/** Exposed for the control that asserts no described capability lands in an unnamed area. */
export function namedCapabilityAreas(): readonly string[] {
  return Object.keys(CAPABILITY_AREA_HE);
}

/** Exposed for the mirror test only. */
export function describedCapabilityCodes(): readonly string[] {
  return Object.keys(CAPABILITY_HE);
}

/**
 * A CATALOGUE LIFECYCLE STATUS, IN HEBREW. **The Owner's finding of 2026-10-20.**
 *
 * `CK_plan_status` and `CK_product_sku_status` are the same three values, and the console asked an
 * operator to TYPE them: *"Plan/package and product lifecycle status must be a labelled selection
 * in Hebrew (active = פעיל, planned = מתוכנן, retired = הוצא משימוש), not typed English codes."*
 *
 * The three are mapped here, beside every other vocabulary this client renders, rather than inside
 * the one screen that shows them — `platformCatalogue` needs them in two forms and two tables, and
 * a second copy in a second form is how two of them come to disagree. The CODE is what travels to
 * the server; this is only what a reader sees.
 */
export const CATALOGUE_STATUSES = ['active', 'planned', 'retired'] as const;
export type CatalogueStatus = (typeof CATALOGUE_STATUSES)[number];

export function catalogueStatusHe(status: string): string {
  switch (status) {
    case 'active':
      return 'פעיל';
    case 'planned':
      return 'מתוכנן';
    case 'retired':
      return 'הוצא משימוש';
    default:
      /*
       * A STATUS THIS BUILD DOES NOT KNOW IS SHOWN AS ITSELF, and that is deliberate: the check
       * constraint admits exactly three, so a fourth means the database moved ahead of this client
       * — and inventing Hebrew for it, or hiding it, would be worse than showing the operator the
       * value their estate actually holds.
       */
      return status;
  }
}

/**
 * A UTC instant as an office date-time.
 *
 * UTC deliberately, and stated on screen as such: 07 §6.2's watermarks are UTC instants, and a
 * freshness line silently re-based to whatever timezone the browser happens to be in is a freshness
 * line two people cannot compare. 09 §9.5 A1: the digits run LTR under an explicit `dir`, never
 * left to the browser's bidi guess — that is the caller's job, and every call site does it.
 */
export function formatInstantHe(iso: string | null): string | null {
  if (iso === null) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const pad = (n: number): string => String(n).padStart(2, '0');
  const date = `${pad(at.getUTCDate())}/${pad(at.getUTCMonth() + 1)}/${at.getUTCFullYear()}`;
  return `${date} ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`;
}
